import { type IdToken, useAuth0 } from "@auth0/auth0-react"
import axios from "axios"
import LogRocket from "logrocket"
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
} from "react"
import { useQuery } from "react-query"
import { useNavigate } from "react-router-dom"

import { useRedux } from "hooks"
import { isLogRocketEnabled } from "lib/logrocket"
import { authenticate } from "redux/actions"
import useUserStore from "stores/user"
import type { User } from "types/auth"
import { getQueryParams } from "utils/jsUtils"
import { getHostInfo } from "utils/networking"
import { setAuthReady } from "./interceptors/axios"

export const AuthContext = createContext<{ user?: User }>({ user: undefined })

const { tenant } = getHostInfo()

const handleMismatchedTokenOrg = (orgName: string) => {
  // Redirect to correct tenant subdomain
  console.error(
    "User is logged in but on the wrong subdomain. Redirecting to tenant subdomain..."
  )

  const { env } = getHostInfo()

  window.location.href = `https://${orgName}.getdashfuel${
    env === "prod" ? "" : env
  }.com${window.location.pathname}`
}

const useAccessToken = ({
  isEnabled,
  onError,
}: {
  isEnabled: boolean
  onError?: (error: unknown) => void
}) => {
  const { getAccessTokenSilently } = useAuth0()

  return useQuery(
    ["token", "access"],
    async () => await getAccessTokenSilently(),
    {
      enabled: isEnabled,
      refetchOnWindowFocus: "always",
      onSuccess: (accessToken) => {
        axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`
        setAuthReady(true)
      },
      onError: (error) => {
        console.error("Failed to get access token", error)
        onError?.(error)
      },
    }
  )
}

const useIdToken = ({
  isEnabled,
  onError,
  onSuccess,
}: {
  isEnabled: boolean
  onError?: (error: unknown) => void
  onSuccess?: (idToken: IdToken | undefined) => void
}) => {
  const { getIdTokenClaims } = useAuth0()

  return useQuery(["token", "id"], getIdTokenClaims, {
    enabled: isEnabled,
    onError: (error) => {
      console.error("No id token, could not check user org", error)
      onError?.(error)
    },
    onSuccess,
  })
}

const useUser = ({ isEnabled }: { isEnabled: boolean }) => {
  return useQuery(
    ["auth", "user"],
    async () => {
      const { data } = await axios.get<User>("auth/user/")

      return data
    },
    {
      enabled: isEnabled,
      onError: (error) => {
        console.error("Error fetching user data", error)
      },
    }
  )
}

const useHandleError = (isEnabled: boolean) => {
  const navigate = useNavigate()
  const { error, error_description } = getQueryParams()

  useEffect(() => {
    if (isEnabled && error && error_description) {
      navigate(`/login?error=${error}&error_description=${error_description}`)
    }
  }, [isEnabled, navigate, error, error_description])
}

const useSetReduxUser = (user?: User) => {
  const { dispatch } = useRedux()
  const setUser = useUserStore(
    (state: { set: (data: User) => void }) => state.set
  )

  useEffect(() => {
    if (user) {
      setUser(user)
      dispatch(authenticate(user))

      if (isLogRocketEnabled()) {
        LogRocket.identify(String(user.id), {
          name: user.full_name || "",
          email: user.email || "",
          userType: user.groups?.[0] || "",
          groups: user.groups?.join(",") || "",
        })
      }
    }
  }, [dispatch, setUser, user])
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0()
  const redirectToLogin = useCallback(
    () =>
      loginWithRedirect({
        appState: {
          returnTo: `${window.location.pathname}${window.location.search}`,
        },
      }),
    []
  )
  const { data: accessToken } = useAccessToken({
    isEnabled: !isLoading && isAuthenticated,
    onError: (error) => {
      console.error("Failed to get access token", error)

      redirectToLogin()
    },
  })
  const { data: idToken } = useIdToken({
    isEnabled: !isLoading && isAuthenticated,
    onSuccess: (idToken) => {
      if (tenant && tenant === idToken?.org_name) return

      if (idToken?.org_name) {
        return handleMismatchedTokenOrg(idToken.org_name)
      }

      console.error(
        "User is logged in but not to any specific org. Redirecting to login..."
      )

      redirectToLogin()
    },
  })
  const { data: user } = useUser({ isEnabled: !!accessToken && !!idToken })

  useHandleError(!isLoading)
  useSetReduxUser(user)

  return (
    <AuthContext.Provider value={{ user }}>{children}</AuthContext.Provider>
  )
}

export const useAuthenticatedUser = () => {
  return useContext(AuthContext)
}
